/** Shared bits for the two wording sections. An EMPTY field means "use the
 *  screen's built-in text", which is why the placeholder shows that default. */
import i18n from 'i18next';
import { Languages } from 'lucide-react';

export const tFor = (lang) => i18n.getFixedT(lang);

/** Built-in fallback for a Goal Game wording key, in the given language. */
export const builtIn = (lang, key) => {
    const value = tFor(lang)(`goalGame.${key}`, '');
    return value === `goalGame.${key}` ? '' : value;
};

/** Full copy object to send: the stored copy with this block's draft applied. */
export const mergeCopy = (stored, draft) => {
    const out = {};
    for (const lang of ['en', 'fr']) {
        out[lang] = { ...((stored || {})[lang] || {}), ...((draft || {})[lang] || {}) };
    }
    return out;
};

/** True while this French field still holds exactly the machine's last
 *  translation — the moment it is edited by hand it stops matching and the
 *  note disappears for good. */
export const isAuto = (cfg, key, value) => {
    const stamp = (cfg?.copy_auto_fr || {})[key];
    return !!stamp && (value || '').trim() === String(stamp).trim();
};

/** English → French, AFTER the save returns. The save is never slowed and the
 *  page is never reloaded: the French boxes simply fill a moment later.
 *  Returns {copy, copy_auto_fr, translated, kept, failed} or null. */
export const autoTranslate = async (api, cfg, keys, force = false) => {
    if (!keys?.length) return null;
    try {
        const res = await api.post('/goal-game/admin/copy/auto-translate', {
            organization_id: cfg.organization_id,
            promotion_id: cfg.promotion_id || null,
            keys, force,
        });
        return res.data;
    } catch (_e) {
        return { translated: [], kept: [], failed: keys };
    }
};

export const CopyField = ({ label, lang, field, value, onChange, textarea, placeholder,
                            testid, auto, failed, onRetranslate }) => (
    <label className="block">
        <span className="block text-xs font-medium text-slate-500 mb-1">
            {label} <span className="uppercase opacity-60">{lang}</span>
        </span>
        {textarea ? (
            <textarea
                rows={2}
                value={value}
                onChange={(e) => onChange(lang, field, e.target.value)}
                placeholder={placeholder || '(default)'}
                data-testid={testid}
                className="w-full rounded-xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 px-3 py-2 text-sm"
            />
        ) : (
            <input
                type="text"
                value={value}
                onChange={(e) => onChange(lang, field, e.target.value)}
                placeholder={placeholder || '(default)'}
                data-testid={testid}
                className="w-full rounded-xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 px-3 py-2 text-sm"
            />
        )}
        {(auto || failed || onRetranslate) && (
            <span className="mt-1 flex flex-wrap items-center gap-2">
                {failed ? (
                    <span className="text-[11px] text-amber-600" data-testid={`${testid}-failed`}>
                        Could not translate, edit by hand
                    </span>
                ) : auto ? (
                    <span className="text-[11px] text-slate-400" data-testid={`${testid}-auto-note`}>
                        Auto-translated · edit to change
                    </span>
                ) : null}
                {onRetranslate && (
                    <button type="button" onClick={() => onRetranslate(field)}
                            data-testid={`${testid}-retranslate`}
                            className="inline-flex items-center gap-1 text-[11px] font-semibold text-slate-500 hover:text-slate-800 dark:hover:text-slate-200">
                        <Languages size={12} /> Translate again
                    </button>
                )}
            </span>
        )}
    </label>
);
